/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { projectProfileEditViewStyles } from './project-profile-edit-view-styles.js';
import { ProjectProfileView } from './project-profile-view.js';
import { FormHostMixin } from '../../modules/form-host-mixin.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/avatar/avatar.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-md-textarea/q1-md-textarea.js';
import '../../components/q1-spinner-panel/q1-spinner-panel.js';

/*=== Q1 MODULES ===*/
import { q1DocFunctions } from '../../modules/q1-core-functions.js';

/*=== CONSTANTS ===*/

// None yet

/*=== VIEW CLASS ===*/

export class ProjectProfileEditView extends FormHostMixin(ProjectProfileView) {
  static styles = [
    ...super.styles,
    projectProfileEditViewStyles,
  ];

  static properties = {
    ...super.properties,
  };

  constructor() {
    super();
  }

  /*=== LIFECYCLE ===*/

  async projectMemberLoaded() {
    super.projectMemberLoaded();

    if (!this.context?.canEdit) this.navigateTo(this.profilePath);
  }

  /*=== ACTIONS ===*/

  discardChanges() {
    if (!confirm('Are you sure you want to discard changes?')) return;
    this.navigateTo(this.profilePath);
  }

  async saveProfile() {
    const profileEditForm = this.shadowRoot.querySelector('#profile-edit-form');
    
    
    if (!profileEditForm.reportValidity()) {
      this.showToast('Please fill out all required fields.', null, 'danger');
      return;
    }

    const { updateDoc } = q1DocFunctions;

    this.processing = true;
    this.error = false;
    this.errorMessage = null;

    try {
      const memberUpdateResult = await updateDoc({
        docCollection: 'projectMembers',
        docId: this.projectMember.id,
        docUpdate: {
          headline: this.projectMember.headline,
          profile: this.projectMember.profile,
        },
      });

      this.showToast('', 'Profile updated successfully!', 'success');
      this.navigateTo(this.profilePath);
    } catch (error) {
      console.error('Error saving profile:', error);
      this.error = true;
      this.errorMessage = error.message;
      this.showToast(error.message, 'There was an error updating your profile!', 'danger');
    } finally {
      this.processing = false;
    }
  }

  /*=== HANDLERS ===*/

  // None yet

  /*=== GETTERS ===*/

  get profilePath() {
    if (!this.projectMember?.key?.includes?.(':')) return null;

    const [projectKey, memberKey] = this.projectMember.key.split(':');
    return `/projects/${projectKey}/@${memberKey}`;
  }

  get loadedDocumentTitle() {
    return `Edit Project Profile: ${this.member.name} - ${this.project.name} - q1 Network`;
  }

  /*=== TEMPLATES ===*/

  get contentHeaderTemplate() {
    if (['init'].includes(this.viewMode)) return null;

    return html`
      <header-bar class="small" id="profile-header-bar">
        ${this.project && this.projectMember ? html`
          <sl-avatar
            image=${this.project.avatar?.small?.url}
            label=${this.project.name}
            shape=${this.project.avatar_shape}
          >
              <sl-icon slot="icon" library="material" name=${this.project.avatar_icon}></sl-icon>
            </sl-avatar>
          </a>
          
          <h1>
            <a class="prefix" href=${this.project.detailViewPath}>${this.project.name}</a>
            Editing Your Project Member Profile
          </h1>
        ` : html`
            <h1>
              <span class="prefix">Project Member Profile</span>
              Project Member Key: ${this.projectMemberKey}
            </h1>
        `}
      </header-bar>
    `;
  }
  
  get viewModeTemplate_loaded() {
    if (!this.member) return null;

    return html`
      ${!this.error ? '' : html`
        <sl-alert variant="danger" open>
          <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
          ${this.errorMessage}
        </sl-alert>
      `}

      <project-profile-content class="sl-themed-secondary">
        <header-bar id="title-header-bar" class="nested">
          <sl-avatar
            image=${this.member.profile?.small?.url}
            label=${this.member.name}
            shape="circle"
          ></sl-avatar>
          
          <div id="member-title">
            <h1>
              ${this.member.name}
            </h1>
            <div id="member-details">
              <span id="memberNumber">@${this.member.number}</span>

              ${!this.member.locationsPrimary_full?.length ? '' : html`
                <span class="separator">—</span>
                
                ${this.member.locationsPrimary_full.map(location => html`
                  <span class="location">
                    <sl-icon library="material" name="home_pin"></sl-icon>
                    ${location.name}
                  </span>
                `)}
              `}
            </div>
          </div>
        </header-bar>
        
        <sl-divider class="full-width"></sl-divider>
        
        <sl-tag size="large" class="visibility-tag" variant="primary">
          <sl-icon
            library=${this.projectMember.visibility_iconLibrary}
            name=${this.projectMember.visibility_icon}
          ></sl-icon>
          ${this.projectMember.visibility_description}
        </sl-tag>

        <form id="profile-edit-form" class="q1">
          <sl-input
            id="headline"
            name="projectMember.headline"
            label="Headline"
            value=${this.projectMember?.headline || ''}
            @input=${this.handleInput}
            required
          ></sl-input>

          <q1-md-textarea
            id="profile"
            name="projectMember.profile"
            label="Profile"
            value=${this.projectMember?.profile || ''}
            rows="20"
            maxlength="2000"
            @input=${this.handleInput}
            required
          ></q1-md-textarea>
          
          <button-bar class="right-align">
            <sl-button
              id="discardChanges"
              @click=${this.discardChanges}
            >
              <sl-icon library="material" name="close"></sl-icon>
              Discard Changes
            </sl-button>
            <sl-button
              id="saveProfile"
              variant="primary"
              @click=${this.saveProfile}
            >
              <sl-icon library="material" name="save"></sl-icon>
              Update Profile
            </sl-button>
          </button-bar>
        </form>
      </project-profile-content>
    `;
  }

}

customElements.define('project-profile-edit-view', ProjectProfileEditView);
